import { useState } from "react";
import { useTranslation } from "@i18n";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import { convertDate } from "../../../../../services/luxonHelper";
import CheckBoxComponent from "@CubeComponents/Mui_Form/CheckBox";
import DetailsIcon from "@Components/StickySidebarViewer/assets/Details";
import UserNameTagComponent from "../../../../../cubeComponent/html/UserNameTagComponent";
import { useSelector } from "react-redux";
import { getSingleTransmittalPageUrl } from "@Features/projects/utils/projectUrls";
import { useNavigate } from "react-router-dom";
import IconComponent from "@CubeComponents/icons";
import { RootState } from "@Reducers/index";

function ReviewLevelTree({
  reviewLevels,
  showCheckboxes = false,
  showProgress = false,
  showNotifyBtn = false,
  changeLevelCheckboxStatusHandler,
  changeReviewerCheckboxStatusHandler,
  notifyUserInReviewCallback,
}: any) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const initialState: any = {};
  reviewLevels.map(({ serial }: any) => (initialState[serial] = true));

  const [reviewLevelsAccState, setReviewLevelsAccState] = useState(initialState);
  const [isHovered, setIsHovered] = useState({});
  const projectData = useSelector((state: RootState) => state.projectData);
  const notifyTimeOut = 86400000;

  const levelTimelineCircleMapper = {
    approved: "checkMark",
    revoked: "revokeMark",
    in_progress: "inProgressMark",
    pending: "defaultMark",
    bypassed: "bypassedMark",
  };

  const reviewerTimelineCircleMapper = {
    in_progress: {
      approve: "checkMark",
      revoke: "revokeMark",
      skip: "skippedMark",
      auto_skipped: "skippedMark",
    },
    approved: {
      approve: "checkMark",
      revoke: "revokeMark",
      skip: "skippedMark",
      auto_skipped: "skippedMark",
    },
    pending: {
      approve: "checkMark",
      revoke: "revokeMark",
      skip: "skippedMark",
      auto_skipped: "skippedMark",
    },
    revoked: {
      approve: "checkMark",
      revoke: "revokeMark",
      skip: "skippedMark",
      auto_skipped: "skippedMark",
    },
    bypassed: {
      approve: "checkMark",
      revoke: "revokeMark",
      skip: "skippedMark",
      auto_skipped: "skippedMark",
    },
  };

  const getNestedChildDiv = (childClassName: any) => {
    if (childClassName === "checkMarkTick") {
      return (
        <div className="absolute start-1/2 top-1/2 h-2 w-1 -translate-x-1/2 -translate-y-1/2 rotate-45 border-b-2 border-e-2 border-white" />
      );
    }

    return <div className="absolute h-4 w-4" />;
  };

  const getInProgressChildDiv = (angle: any) => {
    let angleRect1 = 90;
    let angleRect2 = 90;
    let angleRect3 = 90;
    let angleRect4 = 90;

    if (angle > 0) {
      angleRect1 = 90 - angle > 0 ? 90 - angle : 0;
    }

    if (angle > 90) {
      angleRect2 = 180 - angle > 0 ? 180 - angle : 0;
    }

    if (angle > 180) {
      angleRect3 = 270 - angle > 0 ? 270 - angle : 0;
    }

    if (angle > 270) {
      angleRect4 = 360 - angle > 0 ? 360 - angle : 0;
    }

    const styleRect1 = { transform: `skew(0, -${angleRect1}deg)` };
    const styleRect2 = { transform: `skew(${angleRect2}deg)` };
    const styleRect3 = { transform: `skew(0, -${angleRect3}deg)` };
    const styleRect4 = { transform: `skew(${angleRect4}deg)` };

    return (
      <div className="relative z-[1] flex h-4 w-4 items-center justify-center overflow-hidden rounded-full bg-[#d0f3df]">
        <div className="absolute h-3 w-3 rounded-full bg-[#d0f3df] z-[3]" />
        <div className="absolute start-2 top-0 h-2 w-2 origin-left bg-[#45cf80] z-[2]" style={styleRect1} />
        <div className="absolute start-2 top-2 h-2 w-2 origin-top bg-[#45cf80] z-[2]" style={styleRect2} />
        <div className="absolute start-0 top-2 h-2 w-2 origin-right bg-[#45cf80] z-[2]" style={styleRect3} />
        <div className="absolute start-0 top-0 h-2 w-2 origin-bottom bg-[#45cf80] z-[2]" style={styleRect4} />
      </div>
    );
  };

  const getCrossIconChildDiv = () => (
    <div className="absolute start-0 top-0 z-[1] h-4 w-4 rotate-45">
      <div className="absolute start-0 top-0 z-[2] h-4 w-4 rounded-full bg-red-500 rotate-45" />
      <div className="absolute start-1/2 top-1/2 z-[3] h-2 w-0.5 -translate-x-1/2 -translate-y-1/2 bg-white" />
      <div className="absolute start-1/2 top-1/2 z-[3] h-0.5 w-2 -translate-x-1/2 -translate-y-1/2 bg-white" />
    </div>
  );

  const timelineCircleStyleMapper = {
    checkMark: {
      childClassName: "checkMarkTick",
      lineColor: "#45cf80",
      circleBg: "#45cf80",
      circleBorder: "#45cf80",
      content: "Submitted",
      getNestedChild: getNestedChildDiv,
    },
    inProgressMark: {
      lineColor: "#45cf80",
      circleBg: "#d0f3df",
      circleBorder: "#45cf80",
      content: "In Progress",
      getNestedChild: getInProgressChildDiv,
    },
    skippedMark: {
      lineColor: "#45cf80",
      circleBg: "#ffffff",
      circleBorder: "#45cf80",
      content: "Skipped",
      getNestedChild: getNestedChildDiv,
    },
    revokeMark: {
      lineColor: "#e75555",
      circleBg: "#e75555",
      circleBorder: "#e75555",
      content: "Revoked",
      getNestedChild: getCrossIconChildDiv,
    },
    waitingMark: {
      lineColor: "#666a84",
      circleBg: "#666a84",
      circleBorder: "#b2b5c1",
      content: "Waiting",
      getNestedChild: getNestedChildDiv,
    },
    defaultMark: {
      lineColor: "#ecf3fe",
      circleBg: "#ecf3fe",
      circleBorder: "#ecf3fe",
      content: "",
      getNestedChild: getNestedChildDiv,
    },
    bypassedMark: {
      lineColor: "#ecf3fe",
      circleBg: "#ffffff",
      circleBorder: "#ecf3fe",
      content: "Bypassed",
      getNestedChild: getNestedChildDiv,
    },
  };

  const userResponseRanking = {
    skip: 1,
    auto_skipped: 2,
    approve: 3,
    revoke: 4,
  };

  const changeAccStateHandler = (serial: any) => {
    setReviewLevelsAccState((prev: any) => ({ ...prev, [serial]: !prev[serial] }));
  };

  const calculateLevelTimelineCircleType = (clearanceStatus: any) => {
    const type = showProgress ? levelTimelineCircleMapper[clearanceStatus] : null;
    return type;
  };

  const calculateReviewerTimelineCircleType = (clearanceStatus: any, response: any) => {
    let type;

    if (response) {
      type = reviewerTimelineCircleMapper[clearanceStatus]?.[response];
    } else if (["in_progress"].includes(clearanceStatus)) {
      type = "waitingMark";
    } else if (["pending", "revoked"].includes(clearanceStatus)) {
      type = "defaultMark";
    } else if (["bypassed"].includes(clearanceStatus)) {
      type = "bypassedMark";
    }

    return showProgress ? type : null;
  };

  const getUserAvatarComponent = (user: any) => <UserNameTagComponent id={user.userId} />;

  const getReviewerCheckbox = (_id: any, userId: any, isReviewerSelected: any) => (
    <div className="flex h-4 w-4 items-center justify-center">
      <CheckBoxComponent
        checked={isReviewerSelected}
        onChange={(e) => changeReviewerCheckboxStatusHandler(e, _id, userId)}
      />
    </div>
  );

  const getLevelCheckbox = (_id: any, levelSelectionState: any) => {
    const isChecked = levelSelectionState === "complete";
    const isPartial = levelSelectionState === "partial";

    if (isPartial) {
      return (
        <div className="flex h-4 w-4 items-center justify-center">
          <CheckBoxComponent indeterminate={isPartial} onChange={(e) => changeLevelCheckboxStatusHandler(e, _id)} />
        </div>
      );
    }

    return (
      <div className="flex h-4 w-4 items-center justify-center">
        <CheckBoxComponent checked={isChecked} onChange={(e) => changeLevelCheckboxStatusHandler(e, _id)} />
      </div>
    );
  };

  const getTimelineCircle = (type: any, levelIndex: any, reviewerIndex: any, progressAngle: any) => {
    const zIndex = parseInt(`${levelIndex}${reviewerIndex}`);
    type = type || "defaultMark";
    const { content, childClassName, getNestedChild, lineColor, circleBg, circleBorder } =
      timelineCircleStyleMapper[type];

    const args = type === "inProgressMark" ? progressAngle : childClassName;
    const timelineContent = (
      <div className="relative h-4 w-4" style={{ zIndex }}>
        <div
          className="absolute start-[0.375rem] top-full h-[62.4375rem] w-[0.1875rem] border-s-2"
          style={{ borderColor: lineColor }}
        />
        <div
          className="absolute inset-0 rounded-full border-2"
          style={{ backgroundColor: circleBg, borderColor: circleBorder }}
        />
        <div className="relative z-[1] flex h-4 w-4 items-center justify-center">
          <div className="h-4 w-4">{getNestedChild(args)}</div>
        </div>
      </div>
    );

    if (content) {
      return (
        <>
          <ToolTipComponent showTipPointer position="left" content={content}>
            {timelineContent}
          </ToolTipComponent>
        </>
      );
    }

    return <>{timelineContent}</>;
  };

  const header = (
    _id: any,
    serial: any,
    title: any,
    type: any,
    clearanceStatus: any,
    levelSelectionState: any,
    progressAngle: any,
  ) => (
    <div className="flex items-center justify-start gap-2.5 pb-4 ps-4">
      {!showCheckboxes ? getTimelineCircle(type, serial, 0, progressAngle) : getLevelCheckbox(_id, levelSelectionState)}
      <span className={clearanceStatus === "pending" && showProgress ? "font-semibold opacity-30" : "font-semibold"}>
        {`Level ${serial}`}
      </span>
      <span className={clearanceStatus === "pending" && showProgress ? "opacity-30" : undefined}>{title}</span>
    </div>
  );

  const compareUserResponsesMapper = (a: any, b: any) => {
    const priorityA = a.response ? userResponseRanking[a.response] : 5;
    const priorityB = b.response ? userResponseRanking[b.response] : 5;

    return priorityA - priorityB;
  };

  const getTooltipContent = (clearanceStatus: any, response: any, lastNotified: any, currentTimestamp: any) => {
    if (clearanceStatus !== "in_progress") {
      return t("The user belongs to a level which is not in progress");
    }

    if (response) {
      return t("The user has already submitted their response");
    }

    if (lastNotified && currentTimestamp - lastNotified < notifyTimeOut) {
      return t("You can notify this user again after {{hours, number}} hours", { hours: 24 });
    }
  };

  const handleOpenTransmittalLink = (lastTransmittal: any) => () => {
    const { transmittalId } = lastTransmittal;
    const projectId = projectData._id;

    if (!projectId) {
      return;
    }

    const singleTransmittalPageURL = getSingleTransmittalPageUrl(projectId, transmittalId);

    if (singleTransmittalPageURL) {
      navigate(singleTransmittalPageURL);
    }
  };

  const content = (_id: any, reviewers: any, clearanceStatus: any, serial: any) => {
    const sortedReviewers = [...reviewers];
    if (showProgress) {
      sortedReviewers.sort(compareUserResponsesMapper);
    }

    const currentTimestamp = Date.now();
    return (
      <div className="flex flex-col gap-5 ps-4">
        {sortedReviewers.map((reviewer: any, index: number) => {
          const {
            userId,
            response,
            isReviewerSelected,
            responseTimestamp,
            isUserDeleted,
            comment,
            lastNotified,
            attachedToTransmittal,
            firstName,
            lastName,
          } = reviewer;
          const type = calculateReviewerTimelineCircleType(clearanceStatus, response);

          const lastTransmittal = attachedToTransmittal?.at(-1);

          const enableNotifyBtn =
            (!lastNotified || currentTimestamp - lastNotified >= notifyTimeOut) &&
            clearanceStatus === "in_progress" &&
            !response;
          const tooltipText = enableNotifyBtn
            ? t("Notify User")
            : getTooltipContent(clearanceStatus, response, lastNotified, currentTimestamp);

          return (
            <div
              key={index}
              className="flex flex-col gap-3"
              onMouseEnter={() =>
                setIsHovered((prev: any) => ({
                  ...prev,
                  [`${_id}-${userId}`]: true,
                }))
              }
              onMouseLeave={() =>
                setIsHovered((prev: any) => ({
                  ...prev,
                  [`${_id}-${userId}`]: false,
                }))
              }
            >
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center justify-start gap-2.5 flex-1 min-w-0">
                  {!showCheckboxes
                    ? getTimelineCircle(type, serial, index + 1, undefined)
                    : getReviewerCheckbox(_id, userId, isReviewerSelected)}
                  <div
                    className={
                      clearanceStatus === "pending" && showProgress
                        ? "flex items-center gap-2.5 opacity-30 flex-1 min-w-0"
                        : "flex items-center gap-2.5 flex-1 min-w-0"
                    }
                  >
                    <span
                      className="text-base font-semibold overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                      data-testid="userProfileDetail"
                    >
                      {getUserAvatarComponent(reviewer)}
                    </span>

                    {isUserDeleted && (
                      <ToolTipComponent
                        showTipPointer
                        position="bottom"
                        content={t("The reviewer has been removed from this space")}
                      >
                        <span className="flex items-center">
                          <DetailsIcon size="24" color="#FF0000" />
                        </span>
                      </ToolTipComponent>
                    )}
                    {lastTransmittal ? (
                      <IconComponent
                        position="bottom"
                        toolTipContent={t(
                          "Transmittal TS-{{transmittalIndex}} sent by {{firstName}} {{lastName}}on {{date}}",
                          {
                            transmittalIndex: lastTransmittal.transmittalIndexAtProjectLevel,
                            firstName,
                            lastName: lastName ? `${lastName} ` : "",
                            date: convertDate(Number(lastTransmittal.createdDate), "dateForText"),
                          },
                        )}
                        className="cursor-pointer"
                        name="TransmittalIcon"
                        width="1.5rem"
                        height="1.5rem"
                        onClick={handleOpenTransmittalLink(lastTransmittal)}
                      />
                    ) : undefined}
                    {showNotifyBtn && isHovered[`${_id}-${userId}`] ? (
                      <IconComponent
                        position="bottom"
                        toolTipContent={tooltipText}
                        isEnabled={enableNotifyBtn}
                        onClick={() => notifyUserInReviewCallback(_id, userId)}
                        name="BellIcon"
                        width="1.5rem"
                        height="1.5rem"
                      />
                    ) : undefined}
                  </div>
                </div>
                {responseTimestamp && (
                  <span className="text-xs font-normal flex-shrink-0">
                    {convertDate(Number(responseTimestamp), "dateForText")}
                  </span>
                )}
              </div>
              {comment && ["approve", "revoke", "skip"].includes(response) && (
                <p className="ms-[1.875rem] rounded bg-[#f0f5f9] p-2 text-sm">{comment}</p>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="relative overflow-y-hidden pt-0">
      {reviewLevels.map((reviewLevel: any, index: number) => {
        const { _id, title, serial, reviewers, clearanceStatus, levelSelectionState } = reviewLevel;
        const type = calculateLevelTimelineCircleType(clearanceStatus);
        const className = "!shadow-none !static";
        let angle = 360;

        if (showProgress) {
          const totalReviewers = reviewers.length;
          const reviewersWithResponse = reviewers.filter((reviewer: any) => reviewer.response).length;

          const ratio = reviewersWithResponse / totalReviewers;
          angle = ratio * 360;
        }

        return (
          <Accordion
            key={index}
            disableGutters
            expanded={reviewLevelsAccState[serial]}
            onChange={() => changeAccStateHandler(serial)}
            className={className}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} className="!static">
              {header(_id, serial, title, type, clearanceStatus, levelSelectionState, angle)}
            </AccordionSummary>
            <AccordionDetails className="!pe-0 !pt-0 !pb-4">
              {content(_id, reviewers, clearanceStatus, serial)}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </div>
  );
}

export default ReviewLevelTree;
